import { useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Stack } from "@altimateai/lego";
import DataTable from "@/components/tables/DataTable";
import { Paginate } from "@/components/paginate";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import { getGroupedOpportunities } from "@/helpers/apis";
import { useDiscoverFilters } from "../../hooks";
import { GET_GROUPED_OPPORTUNITIES_DATA } from "../../../constants";
import { GroupedDetailsCell } from "@/pages/explorer/components";
import { MoneyCell, TimeCell } from "@/pages/explorer/components";
import { GetGroupedOpportunitiesDataResponse } from "../../../types";
import { TableHeaderType, parseSortDirection } from "@/components/tables/types";

interface GroupsListWithFilterProps {
  isLoading?: boolean;
}

const GroupsListWithFilter = ({ isLoading: externalLoading }: GroupsListWithFilterProps) => {
  const [searchParams] = useSearchParams();
  const { page, pageSize, setPage, setPageSize, resetPage } = useTablePagination();

  // Get grid sort from URL search params directly
  const gridSort = searchParams.get("sort") || "cost_savings_max";
  const gridSortDirection = parseSortDirection(searchParams.get("sort_order"), "desc");

  // Get filter params from context
  const { groupedFilterParams, isInitialized } = useDiscoverFilters();

  const { data, isFetching: isLoading } = useQuery({
    queryKey: [GET_GROUPED_OPPORTUNITIES_DATA, groupedFilterParams],
    queryFn: () => getGroupedOpportunities(groupedFilterParams),
    enabled: isInitialized,
  });

  const getGroupRowUrl = useCallback(
    (item: GetGroupedOpportunitiesDataResponse) =>
      `/discover?tab=opportunities&opportunity_name=${item.opportunity_name}`,
    []
  );

  // Client-side sorting and pagination for grouped data
  const sortedItems = useMemo(() => {
    const items = data;
    if (!items) return [];
    return items
      .toSorted(
        (a, b) =>
          ((gridSortDirection || "desc") === "desc" ? -1 : 1) *
          ((a[
            (gridSort as keyof GetGroupedOpportunitiesDataResponse) || "cost_savings_max"
          ] as number) -
            (b[
              (gridSort as keyof GetGroupedOpportunitiesDataResponse) || "cost_savings_max"
            ] as number))
      )
      .slice(page * pageSize, (page + 1) * pageSize);
  }, [data, gridSort, gridSortDirection, page, pageSize]);

  const headers: TableHeaderType<GetGroupedOpportunitiesDataResponse>[] = useMemo(
    () => [
      {
        label: "Details",
        uid: "details",
        id: 0,
        width: 350,
        render: (params: GetGroupedOpportunitiesDataResponse) => (
          <GroupedDetailsCell
            resource_type={params.resource_type}
            opportunity_name={params.opportunity_name}
            description={params.description || ""}
            occurrences={params.occurrences}
          />
        ),
      },
      {
        label: "Max Money Savings",
        uid: "cost_savings_max",
        id: 1,
        width: 180,
        render: (params: GetGroupedOpportunitiesDataResponse) => (
          <MoneyCell value={params.cost_savings_max} />
        ),
        sort: {
          sortSearchKey: "sort",
          sortOrderSearchKey: "sort_order",
          sortParamValue: "cost_savings_max",
        },
      },
      {
        label: "Min Money Savings",
        uid: "cost_savings_min",
        id: 2,
        width: 160,
        render: (params: GetGroupedOpportunitiesDataResponse) => (
          <MoneyCell value={params.cost_savings_min} />
        ),
        sort: {
          sortSearchKey: "sort",
          sortOrderSearchKey: "sort_order",
          sortParamValue: "cost_savings_min",
        },
      },
      {
        label: "Max Time Savings",
        uid: "time_savings_max",
        id: 3,
        width: 160,
        render: (params: GetGroupedOpportunitiesDataResponse) => (
          <TimeCell value={params.time_savings_max} />
        ),
        sort: {
          sortSearchKey: "sort",
          sortOrderSearchKey: "sort_order",
          sortParamValue: "time_savings_max",
        },
      },
      {
        label: "Min Time Savings",
        uid: "time_savings_min",
        id: 4,
        width: 160,
        render: (params: GetGroupedOpportunitiesDataResponse) => (
          <TimeCell value={params.time_savings_min} />
        ),
        sort: {
          sortSearchKey: "sort",
          sortOrderSearchKey: "sort_order",
          sortParamValue: "time_savings_min",
        },
      },
    ],
    []
  );

  return (
    <Stack direction="column" gap="lg" align="start" className="al-w-full">
      <div className="al-w-full">
        <DataTable<GetGroupedOpportunitiesDataResponse>
          getRowUrl={getGroupRowUrl}
          header={headers}
          data={{ items: sortedItems }}
          resetPage={resetPage}
          isLoading={isLoading || externalLoading}
        />
        <Paginate
          itemCount={data?.length ?? 0}
          numPages={Math.ceil((data?.length || 0) / pageSize)}
          page={page}
          pageSize={pageSize}
          onPageClick={setPage}
          onPageSizeChange={setPageSize}
          showPageSize
          isLoading={isLoading || externalLoading}
        />
      </div>
    </Stack>
  );
};

export default GroupsListWithFilter;
